import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaShareAlt, FaCalendarAlt, FaArrowRight, FaChevronLeft } from "react-icons/fa";
import { orderAPI } from "../utils/api";

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("accessToken") ? true : false;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { state: { returnUrl: `/my-orders/${orderId}` } });
      return;
    }
    fetchOrderDetail();
  }, [isAuthenticated, navigate, orderId]);

  const fetchOrderDetail = () => {
    setLoading(true);
    orderAPI.getById(orderId)
      .then(response => {
        console.log("주문 상세:", response.data);
        setOrder(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("API 주문 상세 로드 오류:", err);
        const savedOrders = JSON.parse(localStorage.getItem("orders") || "[]");
        
        let foundOrder;
        if (!isNaN(orderId)) {
          foundOrder = savedOrders[parseInt(orderId)];
        } else {
          foundOrder = savedOrders.find(order => (order.id === orderId || order.receipt_id === orderId));
        }
        
        if (foundOrder) {
          setOrder(foundOrder);
        } else {
          setError("주문 내역을 찾을 수 없습니다.");
        }
        setLoading(false);
      });
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      weekday: 'long',
    };
    return new Date(dateString).toLocaleDateString('ko-KR', options);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ko-KR").format(amount);
  };

  const getItemPrice = (item) => {
    if (!item) return 0;
    
    if (item.item && item.options) {
      let price = item.item.price || 0;
      if (item.options && Array.isArray(item.options)) {
        for (const option of item.options) {
          price += option.option?.price || 0;
        }
      }
      return price;
    }
    return item.price || 0;
  };

  const getStatusText = (status) => {
    switch (status) {
      case "PREPARE": return "사찰에서 기도 준비 중";
      case "PROCESS": return "사찰에서 기도 중";
      case "DONE": return "사찰에서 기도 완료됨";
      case "REJECT": return "사찰에서 기도 거절됨";
      case "Cancel": return "기도 취소";
      default: return "기도 준비 중";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "REJECT":
      case "Cancel": return "#FF3B30";
      default: return "var(--main-color)";
    }
  };

  const getProgressPercentage = (status) => {
    switch (status) {
      case "PREPARE": return 25;
      case "PROCESS": return 50;
      case "DONE": return 100;
      default: return 0;
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: '비나이다 기도',
        text: order?.comment || '비나이다에서 기도를 올렸습니다.',
        url: window.location.href,
      });
    } else {
      const commentText = order?.comment || '비나이다에서 기도를 올렸습니다.';
      const shareUrl = window.location.href;
      navigator.clipboard.writeText(`${commentText}\n${shareUrl}`);
      alert('공유 링크가 클립보드에 복사되었습니다.');
    }
  };

  return (
    <div className="bg-light min-vh-100 pt-4 pb-5">
      <Container>
        {/* 헤더 영역 */}
        <Row className="mb-3">
          <Col>
            <Button
              as={Link}
              to="/my-orders"
              variant="link"
              className="text-decoration-none ps-0 mb-2"
              style={{ color: "var(--main-color)" }}
            >
              <FaChevronLeft className="me-1" /> 주문 목록으로 돌아가기
            </Button>
            <div className="d-flex justify-content-between align-items-center">
              <h1 className="h3 fw-bold mb-0">기도 현황</h1>
              {order?.status === "DONE" && (
                <Button
                  variant="light"
                  onClick={handleShare}
                  className="rounded-pill px-3 d-flex align-items-center shadow-sm"
                >
                  <FaShareAlt className="me-2" size={14} /> 공유하기
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5 my-5 bg-white rounded-4 shadow-sm">
            <Spinner
              animation="border"
              role="status"
              style={{ color: "var(--main-color)" }}
            >
              <span className="visually-hidden">로딩 중...</span>
            </Spinner>
            <p className="mt-3 text-muted">기도 현황을 불러오는 중입니다</p>
          </div>
        ) : order ? (
          <>
            {/* 진행 상태 카드 */}
            <Card className="shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
              <div
                className="py-1"
                style={{ backgroundColor: getStatusColor(order.status) }}
              ></div>
              <Card.Body className="py-4 px-4">
                <h2 className="h5 mb-4 d-flex align-items-center">
                  <div
                    className="me-2 rounded-circle"
                    style={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: getStatusColor(order.status),
                    }}
                  ></div>
                  <span style={{ color: getStatusColor(order.status) }}>
                    {getStatusText(order.status)}
                  </span>
                </h2>

                {order.status !== "REJECT" && order.status !== "Cancel" && (
                  <>
                    <div className="mb-3">
                      <ProgressBar
                        now={getProgressPercentage(order.status)}
                        variant="success"
                        style={{
                          height: "10px",
                          borderRadius: "5px",
                          backgroundColor: "#f0f0f0",
                        }}
                      />
                    </div>
                    <div className="d-flex justify-content-between mb-1 px-2">
                      <div className="text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{
                            width: "16px",
                            height: "16px",
                            border: `2px solid ${
                              order.status === "PREPARE"
                                ? "var(--main-color)"
                                : "#ccc"
                            }`,
                            backgroundColor:
                              order.status === "PREPARE"
                                ? "var(--main-color)"
                                : "white",
                          }}
                        >
                          {order.status === "PREPARE" && (
                            <span
                              className="text-white"
                              style={{ fontSize: "10px" }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: "0.8rem",
                            color:
                              order.status === "PREPARE"
                                ? "var(--main-color)"
                                : "#777",
                            fontWeight:
                              order.status === "PREPARE" ? "500" : "normal",
                          }}
                        >
                          기도 준비 중
                        </span>
                      </div>

                      <div className="text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{
                            width: "16px",
                            height: "16px",
                            border: `2px solid ${
                              order.status === "PROCESS"
                                ? "var(--main-color)"
                                : "#ccc"
                            }`,
                            backgroundColor:
                              order.status === "PROCESS"
                                ? "var(--main-color)"
                                : "white",
                          }}
                        >
                          {order.status === "PROCESS" && (
                            <span
                              className="text-white"
                              style={{ fontSize: "10px" }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: "0.8rem",
                            color:
                              order.status === "PROCESS"
                                ? "var(--main-color)"
                                : "#777",
                            fontWeight:
                              order.status === "PROCESS" ? "500" : "normal",
                          }}
                        >
                          기도 진행 중
                        </span>
                      </div>

                      <div className="text-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{
                            width: "16px",
                            height: "16px",
                            border: `2px solid ${
                              order.status === "DONE"
                                ? "var(--main-color)"
                                : "#ccc"
                            }`,
                            backgroundColor:
                              order.status === "DONE"
                                ? "var(--main-color)"
                                : "white",
                          }}
                        >
                          {order.status === "DONE" && (
                            <span
                              className="text-white"
                              style={{ fontSize: "10px" }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: "0.8rem",
                            color:
                              order.status === "DONE"
                                ? "var(--main-color)"
                                : "#777",
                            fontWeight:
                              order.status === "DONE" ? "500" : "normal",
                          }}
                        >
                          기도 완료
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* 기도 사진 카드 (완료 상태일 때만 표시) */}
            {order.status === "DONE" &&
  order.images &&
  order.images.length > 0 && (
    <Card className="shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
      <Card.Body className="py-4 px-4">
        <h2 className="h5 mb-3 fw-bold">기도 사진 보기</h2>
        <div className="overflow-auto">
          <div className="d-flex pb-2" style={{ marginBottom: "10px" }}>
            {order.images.map((imageUrl, index) => {
              // 이미지 URL 로그 추가
              console.log(`기도 사진 ${index + 1}:`, imageUrl);
              
              return (
                <div
                  key={index}
                  className="me-3 position-relative"
                  style={{
                    minWidth: "260px",
                    height: "150px",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                  }}
                  onClick={() => openImageModal(imageUrl)}
                  onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
                  onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  <div
                    className="w-100 h-100 rounded shadow-sm"
                    style={{
                      backgroundImage: `url(${imageUrl || "https://binaida-asset.s3.ap-northeast-2.amazonaws.com/banner/default.jpg"})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundColor: "#f8f8f8",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    {/* 이미지 로드 오류 시 대체 이미지 추가 */}
                    <img
                      src={imageUrl || "https://binaida-asset.s3.ap-northeast-2.amazonaws.com/banner/default.jpg"}
                      alt="기도 사진"
                      onError={(e) => {
                        console.error(`이미지 로드 실패 (${index}):`, imageUrl);
                        e.target.style.display = "none";
                        e.target.parentNode.style.backgroundImage = 
                          "url(https://binaida-asset.s3.ap-northeast-2.amazonaws.com/banner/default.jpg)";
                      }}
                      style={{ display: "none" }}
                    />
                  </div>
                  <div
                    className="position-absolute top-0 end-0 m-2 bg-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "24px",
                      height: "24px",
                      opacity: 0.8,
                    }}
                  >
                    <span style={{ fontSize: "10px" }}>🔍</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card.Body>
    </Card>
  )}

            {/* 주문 내역 카드 */}
            <Card className="shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
              <Card.Body className="py-4 px-4">
                <h2 className="h5 mb-3 fw-bold">주문 내역</h2>

                {/* 주문 날짜 */}
                <div
                  className="p-3 mb-4 d-flex align-items-center justify-content-between rounded"
                  style={{
                    border: "1px solid var(--main-color)",
                    backgroundColor: "#f8fcfc",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaCalendarAlt
                      style={{ color: "var(--main-color)" }}
                      className="me-3"
                    />
                    <span className="fw-medium">
                      {formatDate(order.date || order.created_at || new Date())}
                    </span>
                  </div>
                  <FaArrowRight style={{ color: "#999", opacity: 0.6 }} />
                </div>

                {/* 주문 상품 정보 */}
                <div className="bg-light p-3 rounded">
                  {(order.items || []).map((item, index) => (
                    <div
                      key={index}
                      className={`${index > 0 ? "mt-4 pt-4 border-top" : ""}`}
                    >
                      <h3 className="h6 fw-bold mb-2">
                        {item.item?.name || item.name}
                      </h3>

                      {/* 옵션 정보 */}
                      {(item.options || []).length > 0 && (
                        <div className="ps-2 my-2 border-start border-2">
                          {(item.options || []).map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className="text-muted small mb-1"
                            >
                              • {option.option?.name || option.name}
                              <span className="text-dark">
                                (+
                                {formatCurrency(
                                  option.option?.price || option.price || 0
                                )}
                                원)
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div className="text-muted small">
                          {formatCurrency(getItemPrice(item))}원
                          <span className="ms-1">
                            × {item.count || item.quantity || 1}
                          </span>
                        </div>
                        <div className="fw-medium">
                          {formatCurrency(
                            getItemPrice(item) *
                              (item.count || item.quantity || 1)
                          )}
                          원
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 총 결제액 */}
                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                  <span className="fw-medium">총 결제금액</span>
                  <span
                    className="h5 mb-0 fw-bold"
                    style={{ color: "var(--main-color)" }}
                  >
                    {formatCurrency(order.price || order.total || 0)}원
                  </span>
                </div>
              </Card.Body>
            </Card>

            {/* 기도 말씀 카드 */}
            <Card className="shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
              <Card.Body className="py-4 px-4">
                <h2 className="h5 mb-3 fw-bold">기도 말씀</h2>
                <div className="p-3 bg-light rounded">
                  <p className="mb-0 fst-italic">
                    "{order.comment || "기도 말씀이 없습니다."}"
                  </p>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : (
          <Alert variant="warning" className="mt-4 shadow-sm">
            해당 주문을 찾을 수 없습니다.{" "}
            <Link to="/my-orders">주문 목록으로 돌아가기</Link>
          </Alert>
        )}

        {/* 이미지 모달 */}
        <Modal
          show={showImageModal}
          onHide={() => setShowImageModal(false)}
          centered
          size="lg"
          contentClassName="border-0 rounded-4 overflow-hidden"
        >
          <Modal.Body className="p-0 position-relative">
            <Button
              variant="light"
              className="position-absolute top-0 end-0 m-2 rounded-circle p-1 shadow"
              onClick={() => setShowImageModal(false)}
              style={{ zIndex: 2000, width: "32px", height: "32px" }}
            >
              ✕
            </Button>
            <div className="text-center bg-dark">
              <img
                src={selectedImage}
                alt="기도 사진 확대"
                className="img-fluid"
                style={{ maxHeight: "80vh", objectFit: "contain" }}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button
              variant="primary"
              onClick={() => setShowImageModal(false)}
              className="px-4 rounded-pill"
              style={{
                backgroundColor: "var(--main-color)",
                borderColor: "var(--main-color)",
              }}
            >
              확인
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default OrderDetailPage;